import { useMemo, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import GlassInput from '../ui/GlassInput';
import GlassSelect from '../ui/GlassSelect';
import Field from '../ui/Field';
import { createConcepto } from '../../services/conceptos';
import { useTipoConceptos } from '../../hooks/useCatalogs';
import { todayIsoDate } from '../../utils/formatters';

const INITIAL_VALUES = { name: '', tipo: '' };

function validateName(value) {
  const trimmed = value.trim();
  if (!trimmed) return 'El nombre del concepto es obligatorio.';
  if (trimmed.length < 2) return 'Debe tener al menos 2 caracteres.';
  if (trimmed.length > 100) return 'Máximo 100 caracteres.';
  return null;
}

function validateTipo(value) {
  if (!value) return 'Selecciona un tipo de concepto.';
  return null;
}

function CrearConceptoModal({ isOpen, onClose, onCreated }) {
  const { items: tipos, isLoading: isLoadingTipos, error: tiposError } = useTipoConceptos();
  const [values, setValues] = useState(INITIAL_VALUES);
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const activeTipos = useMemo(
    () => tipos.filter((tipo) => Number(tipo.status) === 1),
    [tipos],
  );

  const nameError = touched.name ? validateName(values.name) : null;
  const tipoError = touched.tipo ? validateTipo(values.tipo) : null;

  const handleClose = () => {
    if (isSubmitting) return;
    setValues(INITIAL_VALUES);
    setTouched({});
    setFeedback(null);
    onClose();
  };

  const handleChange = (field) => (event) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
    if (feedback) setFeedback(null);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nameErr = validateName(values.name);
    const tipoErr = validateTipo(values.tipo);
    setTouched({ name: true, tipo: true });
    if (nameErr || tipoErr) {
      setFeedback({
        type: 'error',
        message: nameErr || tipoErr,
      });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);
    try {
      const result = await createConcepto({
        deConcepto: values.name.trim(),
        tpConcepto: values.tipo,
        feRegistro: todayIsoDate(),
      });
      if (result.ok) {
        setFeedback({
          type: 'success',
          message: 'Se creó el Concepto con éxito.',
        });
        setValues(INITIAL_VALUES);
        setTouched({});
        onCreated?.(result.data);
      } else {
        setFeedback({
          type: 'error',
          message: 'Error en el proceso de gestión de Concepto.',
        });
      }
    } catch {
      setFeedback({
        type: 'error',
        message: 'Error de conexión. Verifica tu red e intenta nuevamente.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nuevo concepto"
      description="Crea un concepto asociado a un tipo"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting}>
            {isSubmitting ? 'Creando…' : 'Crear concepto'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <Field
          label="Nombre del Concepto"
          required
          htmlFor="de-concepto"
          error={touched.name ? nameError : undefined}
          hint="Etiqueta descriptiva del movimiento"
        >
          <GlassInput
            id="de-concepto"
            label="Nombre del Concepto"
            value={values.name}
            onChange={handleChange('name')}
            onBlur={handleBlur('name')}
            isInvalid={Boolean(touched.name && nameError)}
            placeholder="Ej. Salario mensual"
            maxLength={100}
          />
        </Field>
        <Field
          label="Tipo Concepto"
          required
          htmlFor="tp-concepto-concepto"
          error={touched.tipo ? tipoError : undefined}
          hint={tiposError ? 'No se pudieron cargar los tipos.' : 'Categoría del concepto'}
        >
          <GlassSelect
            id="tp-concepto-concepto"
            label="Tipo Concepto"
            options={activeTipos}
            value={values.tipo}
            onChange={handleChange('tipo')}
            onBlur={handleBlur('tipo')}
            isLoading={isLoadingTipos}
            isInvalid={Boolean(touched.tipo && tipoError)}
            placeholder="Selecciona un tipo de concepto"
            emptyMessage="No hay tipos de concepto activos"
          />
        </Field>
        {feedback && (
          <div
            role="status"
            className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm animate-fade-up ${feedback.type === 'success' ? 'border-emerald-300/30 bg-emerald-300/[0.06] text-emerald-100' : 'border-rose-300/30 bg-rose-300/[0.06] text-rose-100'}`}
          >
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-[11px] font-bold">
              {feedback.type === 'success' ? '✓' : '!'}
            </span>
            <p className="text-sm">{feedback.message}</p>
          </div>
        )}
      </form>
    </Modal>
  );
}

export default CrearConceptoModal;